// components/CompanyInfoPage.jsx

const CompanyInfoPage = ({
  register,
  errors,
  backendErrors,
  watch,
  setValue,
  customErrors,
  isFieldRequired,
  shouldShowField,
  companyDisabled,
  commonDisabled,
  isUpdateMode,
  AccountFormService,
  setCustomErrors,
  isParticulier,
  hasError,
  getErrorMessage,
  existingFiles,
  handleRemoveFile
}) => {
  const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif','image/bmp', 'image/webp', 'image/tiff', 'image/svg+xml', 'image/heic'];
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

   // Sécuriser existingFiles
  const safeExistingFiles = existingFiles || {};

  return (
    <div className="page-content">
      <div className="page-header mb-4">
        <h2 className="h4 mb-2">Informations Entreprise</h2>
        <p className="text-muted">
          Renseignez les informations relatives à l'entreprise ou à l'organisation.
        </p>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {shouldShowField('companyName') && (
                <div className="mb-3">
                  <label htmlFor="companyName" className="form-label">
                    Nom de l'entreprise {isFieldRequired('companyName') && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    {...register("companyName", { required: isFieldRequired('companyName') ? "Ce champ est requis." : false })}
                    disabled={companyDisabled}
                    className={`form-control ${
                      errors.companyName || backendErrors.companyName || hasError('companyName') ? "is-invalid" : ""
                    } ${watch("companyName") && !hasError('companyName') ? "is-valid" : ""}`}
                    id="companyName"
                  />
                  {(errors.companyName || hasError('companyName')) && (
                    <div className="invalid-feedback">
                      {errors.companyName?.message || getErrorMessage('companyName')}
                    </div>
                  )}
                </div>
              )}

              {shouldShowField('company_adress') && (
                <div className="mb-3">
                  <label htmlFor="company_adress" className="form-label">
                    Adresse de l'entreprise {isFieldRequired('company_adress') && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    {...register("company_adress", { required: isFieldRequired('company_adress') ? "Ce champ est requis." : false })}
                    disabled={companyDisabled}
                    className={`form-control ${
                      errors.company_adress || backendErrors.company_adress || hasError('company_adress') ? "is-invalid" : ""
                    } ${watch("company_adress") && !hasError('company_adress') ? "is-valid" : ""}`}
                    id="company_adress"
                  />
                  {(errors.company_adress || hasError('company_adress')) && (
                    <div className="invalid-feedback">
                      {errors.company_adress?.message || getErrorMessage('company_adress')}
                    </div>
                  )}
                </div>
              )}

              {/* Afficher le champ logo */}
              {shouldShowField('company_logo') && (
                <div className="mb-3">
                  <label htmlFor="company_logo" className="form-label">
                    Logo de l'entreprise {isFieldRequired('company_logo')}
                  </label>
                  
                  {/* Afficher le fichier existant en mode édition */}
                  {isUpdateMode && safeExistingFiles.companyLogoUrl && (
                    <div className="mb-3 p-3 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-bold text-muted">
                          Logo actuel:
                        </label>
                        <div className="btn-group">
                          <a 
                            href={safeExistingFiles.companyLogoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                            title="Voir le logo"
                          >
                            <i className="fas fa-eye"></i>
                          </a>
                          <button 
                            type="button" 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveFile && handleRemoveFile('company_logo')}
                            title="Supprimer ce logo"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                      <div className="d-flex align-items-center p-2 bg-white rounded border">
                        <i className="fas fa-file-image me-2 text-primary"></i>
                        <span className="text-truncate" style={{ maxWidth: '300px' }}>
                          {safeExistingFiles.companyLogoFileName || 'Logo de l\'entreprise'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    {...register("company_logo", { 
                      required: isUpdateMode ? false : isFieldRequired('company_logo') ? "Ce fichier est requis." : false 
                    })}
                    onChange={(e) => {
                      AccountFormService.validateFile(
                        e.target.files, 
                        "company_logo", 
                        IMAGE_TYPES, 
                        MAX_IMAGE_SIZE, 
                        setCustomErrors
                      );
                    }}
                    accept=".jpg,.jpeg,.png,.gif"
                    id="company_logo"
                    className={`form-control ${
                      errors.company_logo || customErrors.company_logo || hasError('company_logo') ? "is-invalid" : ""
                    } ${
                      (watch("company_logo")?.length > 0 || safeExistingFiles.companyLogoUrl) && 
                      !hasError('company_logo') ? "is-valid" : ""
                    }`}
                    disabled={companyDisabled}
                  />
                  {(errors.company_logo || customErrors.company_logo || hasError('company_logo')) && (
                    <div className="invalid-feedback">
                      {errors.company_logo?.message || customErrors.company_logo || getErrorMessage('company_logo')}
                    </div>
                  )}
                  {watch("company_logo")?.length > 0 && !errors.company_logo && !customErrors.company_logo && !hasError('company_logo') && (
                    <div className="valid-feedback">Logo sélectionné</div>
                  )}
                </div>
              )}

              {/* Champ ISIN avec explication */}
              {shouldShowField('isin') && !isParticulier && (
                <div className="mb-3">
                  <label htmlFor="isin" className="form-label">
                    ISIN (International Securities Identification Number)
                  </label>
                  <input
                    type="text"
                    {...register("isin")}
                    className={`form-control ${
                      errors.isin || backendErrors.isin || hasError('isin') ? 'is-invalid' : ''
                    } ${watch("isin") && !hasError('isin') ? 'is-valid' : ''}`}
                    id="isin"
                    disabled={companyDisabled}
                  />
                  <div className="form-text">
                    <i className="fas fa-info-circle me-1"></i>
                    L'ISIN est un code international unique qui identifie les titres financiers. 
                    Renseignez ce champ uniquement si votre entreprise émet des titres financiers 
                    (actions, obligations, etc.) sur les marchés financiers.
                  </div>
                  {(errors.isin || hasError('isin')) && (
                    <div className="invalid-feedback">
                      {errors.isin?.message || getErrorMessage('isin')}
                    </div>
                  )}
                </div>
              )}

              <div className="row">
                {shouldShowField('legalForm') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="legalForm" className="form-label">
                        Forme juridique {isFieldRequired('legalForm') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="text"
                        {...register("legalForm", { required: isFieldRequired('legalForm') ? "Ce champ est requis." : false })}
                        className={`form-control ${
                          errors.legalForm || backendErrors.legalForm || hasError('legalForm') ? 'is-invalid' : ''
                        } ${watch("legalForm") && !hasError('legalForm') ? 'is-valid' : ''}`}
                        id="legalForm"
                        disabled={commonDisabled}
                      />
                      {(errors.legalForm || hasError('legalForm')) && (
                        <div className="invalid-feedback">
                          {errors.legalForm?.message || getErrorMessage('legalForm')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {shouldShowField('capital') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="capital" className="form-label">
                        Capital {isFieldRequired('capital') && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="number"
                        {...register("capital", { 
                          required: isFieldRequired('capital') ? "Ce champ est requis." : false,
                          min: 0 
                        })}
                        className={`form-control ${
                          errors.capital || backendErrors.capital || hasError('capital') ? 'is-invalid' : ''
                        } ${watch("capital") > 0 && !hasError('capital') ? 'is-valid' : ''}`}
                        id="capital"
                        disabled={commonDisabled}
                      />
                      {(errors.capital || hasError('capital')) && (
                        <div className="invalid-feedback">
                          {errors.capital?.message || getErrorMessage('capital')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Total Outstanding Shares avec explication */}
              {shouldShowField('totalOutstandingShares') && (
                <div className="mb-3">
                  <label htmlFor="totalOutstandingShares" className="form-label">
                    Total Outstanding Shares
                  </label>
                  <input
                    type="number"
                    {...register("totalOutstandingShares", { min: 0 })}
                    className={`form-control ${
                      errors.totalOutstandingShares || backendErrors.totalOutstandingShares || hasError('totalOutstandingShares') ? 'is-invalid' : ''
                    } ${watch("totalOutstandingShares") > 0 && !hasError('totalOutstandingShares') ? 'is-valid' : ''}`}
                    id="totalOutstandingShares"
                    disabled={commonDisabled}
                  />
                  <div className="form-text">
                    <i className="fas fa-info-circle me-1"></i>
                    Le <strong>Total Outstanding Shares</strong> représente le nombre total d'actions 
                    actuellement en circulation et détenues par tous les actionnaires, y compris les blocs d'actions 
                    détenus par des investisseurs institutionnels et les actions restreintes détenues par les dirigeants.
                  </div>
                  {(errors.totalOutstandingShares || hasError('totalOutstandingShares')) && (
                    <div className="invalid-feedback">
                      {errors.totalOutstandingShares?.message || getErrorMessage('totalOutstandingShares')}
                    </div>
                  )}
                </div>
              )}

              <div className="row">
                {shouldShowField('yearlyRevenue') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="yearlyRevenue" className="form-label">Revenu Annuel</label>
                      <input
                        type="number"
                        {...register("yearlyRevenue", { min: 0 })}
                        className={`form-control ${
                          errors.yearlyRevenue || backendErrors.yearlyRevenue || hasError('yearlyRevenue') ? 'is-invalid' : ''
                        } ${watch("yearlyRevenue") > 0 && !hasError('yearlyRevenue') ? 'is-valid' : ''}`}
                        id="yearlyRevenue"
                        disabled={commonDisabled}
                      />
                      {(errors.yearlyRevenue || hasError('yearlyRevenue')) && (
                        <div className="invalid-feedback">
                          {errors.yearlyRevenue?.message || getErrorMessage('yearlyRevenue')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {shouldShowField('cash') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="cash" className="form-label">Cash</label>
                      <input
                        type="number"
                        {...register("cash", { min: 0 })}
                        className={`form-control ${
                          errors.cash || backendErrors.cash || hasError('cash') ? 'is-invalid' : ''
                        } ${watch("cash") > 0 && !hasError('cash') ? 'is-valid' : ''}`}
                        id="cash"
                        disabled={commonDisabled}
                      />
                      {(errors.cash || hasError('cash')) && (
                        <div className="invalid-feedback">
                          {errors.cash?.message || getErrorMessage('cash')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Nouveaux champs pour informations bancaires */}
              <div className="row">
                {shouldShowField('numeroCompteBancaire') && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="numeroCompteBancaire" className="form-label">Numéro de Compte Bancaire</label>
                      <input
                        type="text"
                        {...register("numeroCompteBancaire")}
                        className={`form-control ${
                          errors.numeroCompteBancaire || backendErrors.numeroCompteBancaire || hasError('numeroCompteBancaire') ? 'is-invalid' : ''
                        } ${watch("numeroCompteBancaire") && !hasError('numeroCompteBancaire') ? 'is-valid' : ''}`}
                        id="numeroCompteBancaire"
                        disabled={commonDisabled}
                      />
                      {(errors.numeroCompteBancaire || hasError('numeroCompteBancaire')) && (
                        <div className="invalid-feedback">
                          {errors.numeroCompteBancaire?.message || getErrorMessage('numeroCompteBancaire')}
                        </div>
                      )}
                    </div>
                  </div>
                )} 
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfoPage;